import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, IconButton, Stack, SwipeableDrawer } from '@mui/material';
import { theme } from '../../../../../common/styles/theme';
import { AudioHOC } from '../Audio/hocs/AudioHOC';
import { HeaderHOC } from '../../../ToolbarPanel/Header/hocs/HeaderHOC';
import { Tabs } from '../../../../../common/components/Tabs/Tabs';
import { Tab } from '../../../../../common/components/Tab/Tab';
import { Text } from '../../../../../common/components/Text/Text';
import { MoreIcon } from '../../../../../../icons/MoreIcon';
import { ExercisesTabHOC } from '../ExercisesTab/hocs/ExercisesTabHOC';
import { WordsTabHOC } from '../WordsTab/hocs/WordsTabHOC';
import { BookTabHOC } from '../BookTab/hocs/BookTabHOC';
import { Footer } from '../../../../../common/components/Footer/Footer';
import { LessonTranscriptHOC } from '../LessonTranscript/hocs/LessonTranscriptHOC';
import { TTranscriptHighlight } from '../LessonTranscript/components/types';
import { EAudioPageTab, IAudioPageProps } from './types';
import { Splitter } from '../Splitter/Splitter';
import { CallRecordingProgressModalHOC } from '../../CallRecordingProgressModal/hocs/CallRecordingProgressModalHOC';
import { TranslationsIcon } from '../../../../../../icons/TranslationsIcon';
import { EndlessBookIcon } from '../../../../../../icons/EndlessBookIcon';
import { PlayCaptionIcon } from '../../../../../../icons/PlayCaptionIcon';
import { SchoolIcon } from '../../../../../../icons/SchoolIcon';
import { pillTabsSx } from '../../../../../common/components/Tabs/pillTabsSx';
import { RecordingActionsHOC } from '../RecordingActions/hocs/RecordingActionsHOC';
import { useIsMobileLayout } from '../../../../../common/hooks/useIsMobileLayout';
import { LessonTabBar } from '../LessonTabBar/LessonTabBar';
import { CLASS_PLAY_CONTROL } from '../Audio/components/constants';
import { CloseIcon } from '../../../../../../icons/CloseIcon';

const LEFT_WIDTH_KEY = 'audio-page-left-width';
const LEFT_MIN_WIDTH = 320;
const RIGHT_MIN_WIDTH = 240;

export const AudioPage: React.FC<IAudioPageProps> = ({
  title,
  date,
  audioId: currentAudioId,
  canEditSettings,
  progressLog,
  isProcessing,
  progressStartedAt,
  progressStepStartedAt,
  progressUpdatedAt,
  onChangeRoute,
  onChangeTab,
  onReprocess,
  onCancel,
  onCloseSettings,
  onPlayTimestamp,
  onPlayCaption,
  onPause,
}) => {
  // the same settings popup as the recording tile's "Settings" menu item
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const handleSettingsClose = () => {
    setSettingsOpen(false);
    onCloseSettings();
  };

  const isMobileLayout = useIsMobileLayout();

  const { groupId, audioId, tab } = useParams<{ groupId: string, audioId: string, tab?: string }>();
  const activeTab = (tab as EAudioPageTab) || EAudioPageTab.RECORDING;

  React.useEffect(() => {
    onChangeRoute(+groupId, +audioId);
  }, [groupId, audioId]);

  // set by an exercise/word timestamp or transcript line click: highlights that line and plays it
  const [highlight, setHighlight] = React.useState<TTranscriptHighlight | null>(null);
  // mobile: the transcript is a sheet on the right, opened by a timestamp
  const [transcriptOpen, setTranscriptOpen] = React.useState(false);
  React.useEffect(() => {
    setHighlight(null);
    setTranscriptOpen(false);
  }, [audioId, activeTab]);
  const onTimestampClick = (time: number) => {
    setHighlight({ time });
    onPlayTimestamp(time);
    if (isMobileLayout) setTranscriptOpen(true);
  };
  // the first scroll ran while the sheet was still sliding in — center the line again
  const onTranscriptEntered = () => {
    setHighlight(h => h && { ...h });
  };
  // the sheet is the only visible playback control on the Exercises/Words tabs
  const closeTranscript = () => {
    setTranscriptOpen(false);
    onPause();
  };
  const onLineClick = (index: number) => {
    setHighlight({ index });
    onPlayCaption(index);
  };
  const withTranscript = activeTab === EAudioPageTab.EXERCISES || activeTab === EAudioPageTab.WORDS;

  // width of the Exercises/Words column, dragged via the splitter and remembered per browser
  const columnsRef = React.useRef<HTMLDivElement | null>(null);
  // null = never dragged: both columns split the space in half
  const [leftWidth, setLeftWidth] = React.useState<number | null>(() => {
    try {
      return Number(localStorage.getItem(LEFT_WIDTH_KEY)) || null;
    } catch (e) {
      return null;
    }
  });
  const onSplitterDrag = (clientX: number) => {
    const box = columnsRef.current?.getBoundingClientRect();
    if (!box) return;
    const width = Math.round(Math.min(Math.max(clientX - box.left, LEFT_MIN_WIDTH), box.width - RIGHT_MIN_WIDTH));
    setLeftWidth(width);
    try { localStorage.setItem(LEFT_WIDTH_KEY, String(width)); } catch (e) { /* storage unavailable */ }
  };

  const transcript = <LessonTranscriptHOC highlight={highlight} onLineClick={onLineClick} />;

  // AudioHOC keeps the same place in the tree in both layouts: remounting it would reset the player
  return (
    <Stack
      sx={{
        width: 1,
        height: 1,
        background: theme.palette.grey[100],
      }}
    >
      <HeaderHOC>
        <Stack
          direction={'row'}
          alignItems={'center'}
          gap={.5}
          sx={{
            minWidth: 0,
            maxWidth: '60vw',
          }}
        >
          <Text
            component={'span'}
            sx={{
              fontSize: theme.typography.subtitle2,
              fontWeight: 600,
              color: theme.palette.grey[900],
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Text>
          {canEditSettings &&
            <IconButton
              onClick={() => setSettingsOpen(true)}
              sx={{
                flexShrink: 0,
                p: .5,
                color: theme.palette.grey[400],
                '&:hover, &:active, &:focus': { color: theme.palette.grey[900] },
                '.MuiSvgIcon-root': { width: theme.spacing(2), height: theme.spacing(2) },
              }}
            >
              <MoreIcon />
            </IconButton>
          }
        </Stack>
      </HeaderHOC>
      <Stack
        gap={isMobileLayout ? 1 : .5}
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          p: theme.spacing(1),
        }}
      >
        {!isMobileLayout &&
        <Stack
          direction={'row'}
          alignItems={'center'}
          gap={4}
          rowGap={0}
          sx={{
            flexWrap: 'wrap',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => onChangeTab(+groupId, +audioId, value)}
            sx={pillTabsSx}
          >
            <Tab icon={<PlayCaptionIcon />} iconPosition={'start'} label={'Recording'} value={EAudioPageTab.RECORDING} />
            <Tab icon={<SchoolIcon />} iconPosition={'start'} label={'Exercises'} value={EAudioPageTab.EXERCISES} />
            <Tab icon={<TranslationsIcon />} iconPosition={'start'} label={'Words'} value={EAudioPageTab.WORDS} />
            <Tab icon={<EndlessBookIcon
              sx={{
                transform: 'translateY(1px)',
              }}
            />} iconPosition={'start'} label={'Book'} value={EAudioPageTab.BOOK} />
          </Tabs>
        </Stack>
        }

        {/* stays mounted across tab switches so playback and scroll position survive; hidden via
            visibility, not display:none — the transcript measures its line heights on first load */}
        <Box
          sx={activeTab === EAudioPageTab.RECORDING
            ? { display: 'contents' }
            // overflow: hidden — otherwise its (invisible) overflow scrolls the whole page
            : { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' }
          }
        >
          <AudioHOC autoLoad />
        </Box>

        {withTranscript &&
        <Stack ref={columnsRef} direction={'row'} sx={{ flex: '1 1 auto', minHeight: 0 }}>
          <Stack sx={isMobileLayout
            ? { flex: '1 1 auto', minWidth: 0, minHeight: 0 }
            : { flex: leftWidth ? `0 1 ${leftWidth}px` : '1 1 0', minWidth: LEFT_MIN_WIDTH, minHeight: 0 }
          }>
            {activeTab === EAudioPageTab.EXERCISES && <ExercisesTabHOC onTimestampClick={onTimestampClick} />}
            {activeTab === EAudioPageTab.WORDS && <WordsTabHOC onTimestampClick={onTimestampClick} />}
          </Stack>
          {!isMobileLayout && <>
            <Splitter orientation={'column'} onDrag={onSplitterDrag} />
            {/* minWidth: when the window shrinks, the left column (flex-shrink) gives way, not this one */}
            <Stack direction={'row'} sx={{ flex: '1 1 0', minWidth: RIGHT_MIN_WIDTH, minHeight: 0 }}>
              {transcript}
            </Stack>
          </>}
        </Stack>
        }
        {activeTab === EAudioPageTab.BOOK && <BookTabHOC />}

        {!isMobileLayout && <Footer />}
      </Stack>

      {isMobileLayout && <>
        <LessonTabBar value={activeTab} onChange={(value) => onChangeTab(+groupId, +audioId, value)} />

        {/* className on the root: a tap inside the sheet shouldn't pause playback — closing it does, explicitly */}
        <SwipeableDrawer
          className={CLASS_PLAY_CONTROL}
          anchor={'right'}
          open={withTranscript && transcriptOpen}
          onOpen={() => setTranscriptOpen(true)}
          onClose={closeTranscript}
          disableSwipeToOpen
          SlideProps={{ onEntered: onTranscriptEntered }}
          PaperProps={{
            sx: {
              width: '82%',
              background: theme.palette.grey[50],
            },
          }}
        >
          <Stack direction={'row'} alignItems={'center'} sx={{ flexShrink: 0, pl: 2, pr: 1, py: 1 }}>
            <Text sx={{ flex: 1, fontWeight: 600, fontSize: theme.typography.subtitle2 }}>Transcript</Text>
            <IconButton onClick={closeTranscript} sx={{ color: theme.palette.grey[500] }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack direction={'row'} sx={{ flex: '1 1 auto', minHeight: 0 }}>
            {withTranscript && transcript}
          </Stack>
        </SwipeableDrawer>
      </>}

      {settingsOpen &&
        <CallRecordingProgressModalHOC
          open
          onClose={handleSettingsClose}
          title={date}
          progressLog={progressLog}
          isProcessing={isProcessing}
          startedAt={progressStartedAt}
          stepStartedAt={progressStepStartedAt}
          updatedAt={progressUpdatedAt}
          audioId={currentAudioId}
          settingsSection={'info'}
          actions={<RecordingActionsHOC />}
          onProcess={onReprocess}
          onCancel={onCancel}
        />
      }
    </Stack>
  );
};
